import { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";
import { Download, TrendingUp, TrendingDown, Scale, ChevronDown, ChevronRight } from "lucide-react";
import * as XLSX from "xlsx";

function money(n) {
  return new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);
}

/* ── KPI ── */
function KPI({ label, value, color, sub, icon: Icon }) {
  return (
    <div style={{ flex: 1, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 22px", boxShadow: "0 1px 6px #0000000a" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase" }}>{label}</div>
        {Icon && <div style={{ width: 34, height: 34, borderRadius: 8, background: color + "15", display: "flex", alignItems: "center", justifyContent: "center" }}><Icon size={16} color={color} /></div>}
      </div>
      <div style={{ fontSize: 24, fontWeight: 800, color: color || "#0d1117" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

/* ── Sección colapsable ── */
function Seccion({ titulo, total, color, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 4 }}>
      <div onClick={() => setOpen(!open)}
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", background: "#f8fafc", cursor: "pointer", borderRadius: open ? "8px 8px 0 0" : 8, border: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {open ? <ChevronDown size={14} color="#94a3b8" /> : <ChevronRight size={14} color="#94a3b8" />}
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0d1117" }}>{titulo}</span>
        </div>
        <span style={{ fontSize: 14, fontWeight: 800, color: color || "#0d1117" }}>{money(total)}</span>
      </div>
      {open && (
        <div style={{ border: "1px solid #e2e8f0", borderTop: "none", borderRadius: "0 0 8px 8px", overflow: "hidden" }}>
          {children}
        </div>
      )}
    </div>
  );
}

/* ── Fila de cuenta ── */
function FilaCuenta({ codigo, nombre, monto, nivel = 0, bold = false }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: `9px ${20 + nivel * 16}px`, borderBottom: "1px solid #f8fafc", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        {codigo && <span style={{ fontFamily: "monospace", fontSize: 11, color: "#29abe2", fontWeight: 700, minWidth: 56 }}>{codigo}</span>}
        <span style={{ fontSize: 13, color: "#374151", fontWeight: bold ? 700 : 400 }}>{nombre}</span>
      </div>
      <span style={{ fontSize: 13, fontWeight: bold ? 800 : 600, color: "#0d1117", whiteSpace: "nowrap" }}>{money(monto)}</span>
    </div>
  );
}

/* ── Fila total ── */
function FilaTotal({ label, monto, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px", background: "#f8fafc", borderTop: "2px solid #e2e8f0" }}>
      <span style={{ fontSize: 13, fontWeight: 800, color: "#0d1117", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: 15, fontWeight: 800, color: color || "#0d1117" }}>{money(monto)}</span>
    </div>
  );
}

export default function BalanceGeneral() {
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState("");
  const [periodos, setPeriodos] = useState([]);
  const [vista, setVista] = useState("situacion"); // situacion | resultados
  const [data, setData] = useState({ cuentas: [], ingresos: 0, gastos: 0 });

  useEffect(() => { cargar(); }, [periodo]);

  async function cargar() {
    setLoading(true);

    // Traer todas las líneas de asiento con datos de cuenta
    let q = supabase.from("asiento_detalle").select(`
      *,
      asientos!inner(fecha, periodo, estado, tipo)
    `).eq("asientos.estado", "contabilizado");

   if (periodo) {
  const anio = periodo.substring(0, 4);
  q = q.gte("asientos.periodo", `${anio}-01`)
       .lte("asientos.periodo", periodo);
}
    const { data: lineas } = await q;

    // Traer plan de cuentas
    const { data: plan } = await supabase.from("plan_cuentas").select("*").eq("activa", true).order("codigo");

    // Agrupar saldos por cuenta
    const saldos = {};
    (lineas || []).forEach(l => {
      const k = l.cuenta_codigo;
      if (!saldos[k]) saldos[k] = { debe: 0, haber: 0 };
      saldos[k].debe += l.debe || 0;
      saldos[k].haber += l.haber || 0;
    });

    // Calcular saldo por naturaleza
    const cuentas = (plan || []).map(c => {
      const s = saldos[c.codigo] || { debe: 0, haber: 0 };
      const saldo = c.naturaleza === "deudora"
        ? s.debe - s.haber
        : s.haber - s.debe;
      return { ...c, saldo: Math.max(saldo, 0), debe: s.debe, haber: s.haber };
    });

    // Periodos únicos
    const { data: ps } = await supabase.from("asientos").select("periodo").order("periodo", { ascending: false });
    setPeriodos([...new Set((ps || []).map(p => p.periodo).filter(Boolean))]);

    setData({ cuentas });
    setLoading(false);
  }

  function getCuenta(codigo) {
    return data.cuentas.find(c => c.codigo === codigo);
  }

  function getSaldo(codigo) {
    return data.cuentas.find(c => c.codigo === codigo)?.saldo || 0;
  }

  function getGrupo(prefijo) {
    return data.cuentas
      .filter(c => c.codigo.startsWith(prefijo) && c.nivel === 3 && c.saldo > 0)
      .sort((a, b) => a.codigo.localeCompare(b.codigo));
  }

  function sumaGrupo(prefijo) {
    return getGrupo(prefijo).reduce((s, c) => s + c.saldo, 0);
  }

  // Activos
  const activoCorriente = sumaGrupo("1.1");
  const activoNoCorriente = sumaGrupo("1.2");
  const totalActivo = activoCorriente + activoNoCorriente;

  // Pasivos
  const pasivoCorriente = sumaGrupo("2.1");
  const pasivoNoCorriente = sumaGrupo("2.2");
  const totalPasivo = pasivoCorriente + pasivoNoCorriente;

  // Patrimonio
  const totalPatrimonio = sumaGrupo("3");
  const resultadoEjercicio = sumaGrupo("4") - sumaGrupo("5");
  const totalPatrimonioConResultado = totalPatrimonio + resultadoEjercicio;

  // Estado de Resultados
  const ingresos = getGrupo("4");
  const gastos = getGrupo("5");
  const totalIngresos = ingresos.reduce((s, c) => s + c.saldo, 0);
  const totalGastos = gastos.reduce((s, c) => s + c.saldo, 0);
  const utilidad = totalIngresos - totalGastos;

  function exportarExcel() {
    const rows = [
      { Sección: "ACTIVO CORRIENTE", Cuenta: "", Monto: "" },
      ...getGrupo("1.1").map(c => ({ Sección: "", Cuenta: `${c.codigo} ${c.nombre}`, Monto: c.saldo })),
      { Sección: "Total Activo Corriente", Cuenta: "", Monto: activoCorriente },
      { Sección: "ACTIVO NO CORRIENTE", Cuenta: "", Monto: "" },
      ...getGrupo("1.2").map(c => ({ Sección: "", Cuenta: `${c.codigo} ${c.nombre}`, Monto: c.saldo })),
      { Sección: "Total Activo No Corriente", Cuenta: "", Monto: activoNoCorriente },
      { Sección: "TOTAL ACTIVO", Cuenta: "", Monto: totalActivo },
      { Sección: "", Cuenta: "", Monto: "" },
      { Sección: "PASIVO CORRIENTE", Cuenta: "", Monto: "" },
      ...getGrupo("2.1").map(c => ({ Sección: "", Cuenta: `${c.codigo} ${c.nombre}`, Monto: c.saldo })),
      { Sección: "Total Pasivo Corriente", Cuenta: "", Monto: pasivoCorriente },
      { Sección: "PATRIMONIO", Cuenta: "", Monto: "" },
      ...getGrupo("3").map(c => ({ Sección: "", Cuenta: `${c.codigo} ${c.nombre}`, Monto: c.saldo })),
      { Sección: "Resultado del Ejercicio", Cuenta: "", Monto: resultadoEjercicio },
      { Sección: "TOTAL PASIVO + PATRIMONIO", Cuenta: "", Monto: totalPasivo + totalPatrimonioConResultado },
    ];
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Balance General");
    XLSX.writeFile(wb, `balance_general_${periodo || "total"}_${new Date().toISOString().split("T")[0]}.xlsx`);
  }

  return (
    <div style={{ padding: "28px 32px", fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0d1117", margin: 0 }}>Balance General</h1>
          <p style={{ color: "#94a3b8", fontSize: 13, margin: "4px 0 0" }}>TuAplicacion SpA · Estado financiero completo</p>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <select value={periodo} onChange={e => setPeriodo(e.target.value)}
            style={{ padding: "9px 14px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontFamily: "inherit", outline: "none", color: "#0d1117", appearance: "none", background: "#fff" }}>
          <option value="">Todos los años</option>
{periodos.map(p => <option key={p} value={p}>Acum. hasta {p}</option>)}
          </select>
          <button onClick={exportarExcel}
            style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 16px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "#fff", color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            <Download size={14} /> Excel
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 14, marginBottom: 24 }}>
        <KPI icon={TrendingUp} label="Total Ingresos" value={money(totalIngresos)} color="#10b981" sub="Neto acumulado" />
        <KPI icon={TrendingDown} label="Total Gastos" value={money(totalGastos)} color="#ef4444" sub="Neto acumulado" />
        <KPI icon={Scale} label="Utilidad / Pérdida" value={money(utilidad)} color={utilidad >= 0 ? "#10b981" : "#ef4444"} sub={utilidad >= 0 ? "Utilidad del ejercicio" : "Pérdida del ejercicio"} />
        <KPI label="Total Activo" value={money(totalActivo)} color="#29abe2" sub={`Pasivo+Patrim: ${money(totalPasivo + totalPatrimonioConResultado)}`} />
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, background: "#f8fafc", borderRadius: 10, padding: 4, border: "1px solid #e2e8f0", width: "fit-content" }}>
        {[{ id: "situacion", label: "Balance de Situación" }, { id: "resultados", label: "Estado de Resultados" }].map(({ id, label }) => (
          <button key={id} onClick={() => setVista(id)}
            style={{ padding: "8px 20px", borderRadius: 7, border: "none", background: vista === id ? "#fff" : "transparent", color: vista === id ? "#0d1117" : "#94a3b8", fontSize: 12, fontWeight: vista === id ? 700 : 500, cursor: "pointer", fontFamily: "inherit", boxShadow: vista === id ? "0 1px 4px #00000010" : "none", transition: "all .15s" }}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: 48, textAlign: "center", color: "#94a3b8" }}>Cargando...</div>
      ) : vista === "situacion" ? (
        /* ── BALANCE DE SITUACIÓN ── */
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {/* ACTIVO */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0d1117", marginBottom: 12, padding: "0 4px" }}>ACTIVO</div>
            <Seccion titulo="Activo Corriente" total={activoCorriente} color="#29abe2">
              {getGrupo("1.1").map(c => <FilaCuenta key={c.codigo} codigo={c.codigo} nombre={c.nombre} monto={c.saldo} />)}
              <FilaTotal label="Total Activo Corriente" monto={activoCorriente} color="#29abe2" />
            </Seccion>
            <div style={{ height: 12 }} />
            <Seccion titulo="Activo No Corriente" total={activoNoCorriente} color="#29abe2">
              {getGrupo("1.2").map(c => <FilaCuenta key={c.codigo} codigo={c.codigo} nombre={c.nombre} monto={c.saldo} />)}
              <FilaTotal label="Total Activo No Corriente" monto={activoNoCorriente} color="#29abe2" />
            </Seccion>
            <div style={{ marginTop: 12, padding: "14px 20px", background: "#0d1117", borderRadius: 10, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: 1 }}>TOTAL ACTIVO</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: "#29abe2" }}>{money(totalActivo)}</span>
            </div>
          </div>

          {/* PASIVO + PATRIMONIO */}
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: "#0d1117", marginBottom: 12, padding: "0 4px" }}>PASIVO + PATRIMONIO</div>
            <Seccion titulo="Pasivo Corriente" total={pasivoCorriente} color="#ef4444">
              {getGrupo("2.1").map(c => <FilaCuenta key={c.codigo} codigo={c.codigo} nombre={c.nombre} monto={c.saldo} />)}
              {getGrupo("2.1").length === 0 && <div style={{ padding: "12px 20px", fontSize: 12, color: "#94a3b8" }}>Sin pasivos registrados</div>}
              <FilaTotal label="Total Pasivo Corriente" monto={pasivoCorriente} color="#ef4444" />
            </Seccion>
            <div style={{ height: 12 }} />
            <Seccion titulo="Patrimonio" total={totalPatrimonioConResultado} color="#10b981">
              {getGrupo("3").map(c => <FilaCuenta key={c.codigo} codigo={c.codigo} nombre={c.nombre} monto={c.saldo} />)}
              <FilaCuenta codigo="" nombre="Resultado del ejercicio" monto={resultadoEjercicio} bold />
              <FilaTotal label="Total Patrimonio" monto={totalPatrimonioConResultado} color="#10b981" />
            </Seccion>
            <div style={{ marginTop: 12, padding: "14px 20px", background: "#0d1117", borderRadius: 10, display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: 14, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: 1 }}>TOTAL PAS + PAT</span>
              <span style={{ fontSize: 16, fontWeight: 800, color: totalActivo === totalPasivo + totalPatrimonioConResultado ? "#10b981" : "#ef4444" }}>
                {money(totalPasivo + totalPatrimonioConResultado)}
              </span>
            </div>
            {/* Verificación */}
            <div style={{ marginTop: 8, textAlign: "center" }}>
              <span style={{ background: totalActivo === totalPasivo + totalPatrimonioConResultado ? "#e8f5e9" : "#fce4ec", color: totalActivo === totalPasivo + totalPatrimonioConResultado ? "#2e7d32" : "#c62828", borderRadius: 6, padding: "4px 14px", fontSize: 11, fontWeight: 700 }}>
                {totalActivo === totalPasivo + totalPatrimonioConResultado ? "✓ Balance cuadrado" : "✗ Balance descuadrado"}
              </span>
            </div>
          </div>
        </div>
      ) : (
        /* ── ESTADO DE RESULTADOS ── */
        <div style={{ maxWidth: 700 }}>
          <Seccion titulo="Ingresos Operacionales" total={totalIngresos} color="#10b981">
            {ingresos.map(c => <FilaCuenta key={c.codigo} codigo={c.codigo} nombre={c.nombre} monto={c.saldo} />)}
            {ingresos.length === 0 && <div style={{ padding: "12px 20px", fontSize: 12, color: "#94a3b8" }}>Sin ingresos registrados</div>}
            <FilaTotal label="Total Ingresos" monto={totalIngresos} color="#10b981" />
          </Seccion>

          <div style={{ height: 12 }} />

          <Seccion titulo="Gastos Operacionales" total={totalGastos} color="#ef4444">
            {gastos.map(c => <FilaCuenta key={c.codigo} codigo={c.codigo} nombre={c.nombre} monto={c.saldo} />)}
            {gastos.length === 0 && <div style={{ padding: "12px 20px", fontSize: 12, color: "#94a3b8" }}>Sin gastos registrados</div>}
            <FilaTotal label="Total Gastos" monto={totalGastos} color="#ef4444" />
          </Seccion>

          <div style={{ marginTop: 16, padding: "20px 24px", background: utilidad >= 0 ? "#f0fdf4" : "#fef2f2", border: `2px solid ${utilidad >= 0 ? "#10b981" : "#ef4444"}`, borderRadius: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>
                  {utilidad >= 0 ? "UTILIDAD DEL EJERCICIO" : "PÉRDIDA DEL EJERCICIO"}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  {periodo ? `Período ${periodo}` : "Acumulado total"}
                </div>
              </div>
              <div style={{ fontSize: 28, fontWeight: 800, color: utilidad >= 0 ? "#10b981" : "#ef4444" }}>
                {money(Math.abs(utilidad))}
              </div>
            </div>
            {totalIngresos > 0 && (
              <div style={{ marginTop: 12, display: "flex", gap: 20, fontSize: 12, color: "#94a3b8" }}>
                <span>Margen: <strong style={{ color: utilidad >= 0 ? "#10b981" : "#ef4444" }}>{Math.round((utilidad / totalIngresos) * 100)}%</strong></span>
                <span>Ingresos: <strong style={{ color: "#0d1117" }}>{money(totalIngresos)}</strong></span>
                <span>Gastos: <strong style={{ color: "#0d1117" }}>{money(totalGastos)}</strong></span>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');`}</style>
    </div>
  );
}